import { useUser } from '@clerk/clerk-react'
import { zodResolver } from '@hookform/resolvers/zod'
import * as Sentry from '@sentry/react'
import { Eye, EyeOff } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'

import { PasswordStrengthMeter } from '@/components/account/PasswordStrengthMeter'
import { Button } from '@/components/ui/button'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { useAuthToken } from '@/hooks/useAuthToken'
import { MIN_PASSWORD_LENGTH, evaluatePassword } from '@/lib/password'
import { UpdatePasswordError, meService } from '@/services/meService'

const resolveUpdatePasswordErrorMessage = (
  err: UpdatePasswordError,
  t: (key: string, opts?: Record<string, unknown>) => string,
  fallback: string
): string => {
  // 400 = incorrect current password (domain error from our backend).
  if (err.status === 400) {
    return err.message || t('setupPassword.errorMessage')
  }
  // 422 = Clerk's password policy rejected the new password. The clerkCode
  // lets us localize specific reasons; default to Clerk's message otherwise.
  if (err.status === 422 && err.clerkCode) {
    const key = `setupPassword.clerkErrors.${err.clerkCode}`
    const translated = t(key)
    if (translated && translated !== key) {
      return translated
    }
  }
  return err.message || fallback
}

const buildSchema = (t: (key: string, opts?: Record<string, unknown>) => string, requireCurrent: boolean) =>
  z
    .object({
      currentPassword: z.string(),
      newPassword: z
        .string()
        .min(MIN_PASSWORD_LENGTH, t('setupPassword.validation.minLength', { count: MIN_PASSWORD_LENGTH })),
      confirmPassword: z.string(),
    })
    .refine((data) => !requireCurrent || data.currentPassword.length > 0, {
      message: t('accountDialog.changePassword.currentPasswordRequired'),
      path: ['currentPassword'],
    })
    .refine((data) => data.newPassword === data.confirmPassword, {
      message: t('setupPassword.validation.mismatch'),
      path: ['confirmPassword'],
    })

type FormValues = z.infer<ReturnType<typeof buildSchema>>

export function ChangePasswordForm({ onCancel }: { readonly onCancel?: () => void }) {
  const { t } = useTranslation()
  const { user, isLoaded } = useUser()
  const { getToken } = useAuthToken()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showCurrent, setShowCurrent] = useState(false)
  const [showNew, setShowNew] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)

  const passwordEnabled = user?.passwordEnabled ?? false
  const schema = useMemo(() => buildSchema(t, passwordEnabled), [t, passwordEnabled])

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
    mode: 'onChange',
  })

  const newPassword = form.watch('newPassword')
  const confirmPassword = form.watch('confirmPassword')
  const currentPassword = form.watch('currentPassword')

  const userInputs = useMemo(
    () =>
      [user?.firstName, user?.lastName, user?.primaryEmailAddress?.emailAddress, user?.username].filter(
        (v): v is string => Boolean(v)
      ),
    [user?.firstName, user?.lastName, user?.primaryEmailAddress?.emailAddress, user?.username]
  )

  const strength = useMemo(() => evaluatePassword(newPassword, userInputs), [newPassword, userInputs])
  const passwordsMatch = newPassword.length > 0 && newPassword === confirmPassword
  const canSubmit =
    strength.meetsAll && passwordsMatch && (!passwordEnabled || currentPassword.length > 0) && !isSubmitting

  const titleKey = passwordEnabled ? 'accountDialog.changePassword.title' : 'accountDialog.setPassword.title'
  const submitKey = passwordEnabled
    ? 'accountDialog.changePassword.submitButton'
    : 'accountDialog.setPassword.submitButton'
  const successKey = passwordEnabled
    ? 'accountDialog.changePassword.successMessage'
    : 'accountDialog.setPassword.successMessage'

  const onSubmit = async (values: FormValues) => {
    if (!user) return
    setIsSubmitting(true)
    try {
      const token = await getToken()
      if (!token) {
        toast.error(t('setupPassword.errorMessage'))
        return
      }
      await meService.updatePassword(token, {
        new_password: values.newPassword,
        ...(passwordEnabled ? { current_password: values.currentPassword } : {}),
      })
      // Reload the Clerk user so the parent SecurityTab re-renders with
      // the updated passwordEnabled flag and shows the new password-is-set
      // state.
      await user.reload()
      form.reset()
      toast.success(t(successKey))
      // Exit editing mode so the user returns to the read-only Security
      // view (which now reflects the change). Leaving them in the form
      // with empty fields after a successful submit reads as "nothing
      // happened" — see ENG-2590 follow-up.
      onCancel?.()
    } catch (err) {
      if (err instanceof UpdatePasswordError) {
        // 400 = wrong current password (user-correctable, don't report).
        // 422 = Clerk policy rejection (user-correctable, don't report).
        // Anything else is unexpected — capture for triage.
        if (err.status !== 400 && err.status !== 422) {
          Sentry.captureException(err, { tags: { feature: 'password-update' } })
        }
        toast.error(resolveUpdatePasswordErrorMessage(err, t, t('setupPassword.errorMessage')))
      } else {
        Sentry.captureException(err, { tags: { feature: 'password-update' } })
        toast.error(t('setupPassword.errorMessage'))
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isLoaded) return null

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-4'>
        <input
          type='email'
          name='username'
          autoComplete='username'
          value={user?.primaryEmailAddress?.emailAddress ?? ''}
          readOnly
          aria-hidden='true'
          tabIndex={-1}
          className='sr-only'
        />
        <h3 className='text-base font-semibold'>{t(titleKey)}</h3>

        {passwordEnabled && (
          <FormField
            control={form.control}
            name='currentPassword'
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('accountDialog.changePassword.currentPasswordLabel')}</FormLabel>
                <FormControl>
                  <div className='relative'>
                    <Input type={showCurrent ? 'text' : 'password'} autoComplete='current-password' {...field} />
                    <button
                      type='button'
                      aria-label={showCurrent ? t('setupPassword.hidePassword') : t('setupPassword.showPassword')}
                      className='absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground hover:text-foreground'
                      onClick={() => setShowCurrent(!showCurrent)}
                      tabIndex={-1}
                    >
                      {showCurrent ? <EyeOff className='h-4 w-4' /> : <Eye className='h-4 w-4' />}
                    </button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <FormField
          control={form.control}
          name='newPassword'
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('setupPassword.newPassword')}</FormLabel>
              <FormControl>
                <div className='relative'>
                  <Input type={showNew ? 'text' : 'password'} autoComplete='new-password' {...field} />
                  <button
                    type='button'
                    aria-label={showNew ? t('setupPassword.hidePassword') : t('setupPassword.showPassword')}
                    className='absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground hover:text-foreground'
                    onClick={() => setShowNew(!showNew)}
                    tabIndex={-1}
                  >
                    {showNew ? <EyeOff className='h-4 w-4' /> : <Eye className='h-4 w-4' />}
                  </button>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <PasswordStrengthMeter strength={strength} hasInput={newPassword.length > 0} />

        <FormField
          control={form.control}
          name='confirmPassword'
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('setupPassword.confirmPassword')}</FormLabel>
              <FormControl>
                <div className='relative'>
                  <Input type={showConfirm ? 'text' : 'password'} autoComplete='new-password' {...field} />
                  <button
                    type='button'
                    aria-label={showConfirm ? t('setupPassword.hidePassword') : t('setupPassword.showPassword')}
                    className='absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground hover:text-foreground'
                    onClick={() => setShowConfirm(!showConfirm)}
                    tabIndex={-1}
                  >
                    {showConfirm ? <EyeOff className='h-4 w-4' /> : <Eye className='h-4 w-4' />}
                  </button>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className='flex gap-3'>
          {onCancel && (
            <Button type='button' variant='secondary' className='flex-1' onClick={onCancel}>
              {t('accountDialog.changePassword.cancel')}
            </Button>
          )}
          <Button type='submit' variant='manage' disabled={!canSubmit} className='flex-1'>
            {isSubmitting ? t('accountDialog.changePassword.submitting') : t(submitKey)}
          </Button>
        </div>
      </form>
    </Form>
  )
}
